import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Plus, Search, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { sellerService } from "@/features/sellers/services/seller.service";
import { rentalService } from "@/features/rentals/services/rental.service";
import { SellersTable } from "@/features/sellers/components/SellersTable";
import { SellerFormDialog } from "@/features/sellers/components/SellerFormDialog";
import { SellerDeleteDialog } from "@/features/sellers/components/SellerDeleteDialog";
import { SellerRentalsModal } from "@/features/sellers/components/SellerRentalsModal";
import type { SellerResponseDTO } from "@/features/sellers/types/seller.types";
import type { RentalResponseDTO } from "@/features/rentals/types/rental.types";
import { formatCPF } from "@/shared/lib/formatters";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";

type DialogMode = "create" | "edit" | "delete" | null;

const sellerSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres").max(100),
  document: z.string().min(1, "Documento é obrigatório").max(20),
});

type SellerFormData = z.infer<typeof sellerSchema>;

export function VendedoresPage() {
  const [sellers, setSellers] = useState<SellerResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [quickSearch, setQuickSearch] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterDocument, setFilterDocument] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [appliedDocument, setAppliedDocument] = useState("");

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedSeller, setSelectedSeller] =
    useState<SellerResponseDTO | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [isRentalsModalOpen, setIsRentalsModalOpen] = useState(false);
  const [sellerRentals, setSellerRentals] = useState<RentalResponseDTO[]>([]);
  const [rentalsLoading, setRentalsLoading] = useState(false);
  const [rentalsError, setRentalsError] = useState(false);
  const [rentalsTotal, setRentalsTotal] = useState(0);

  const form = useForm<SellerFormData>({
    resolver: zodResolver(sellerSchema),
    defaultValues: { name: "", document: "" },
  });

  const filteredSellers = useMemo(() => {
    if (!quickSearch) return sellers;
    const term = quickSearch.toLowerCase();
    const termDigits = quickSearch.replace(/\D/g, "");
    return sellers.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        (termDigits && s.document.replace(/\D/g, "").includes(termDigits)),
    );
  }, [sellers, quickSearch]);

  const loadSellers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await sellerService.search({
        name: appliedName || undefined,
        document: appliedDocument || undefined,
        page: currentPage,
        size: pageSize,
      });
      setSellers(result.content);
      setTotalPages(result.totalPages);
      setTotalElements(result.totalElements);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar vendedores"));
    } finally {
      setLoading(false);
    }
  }, [appliedName, appliedDocument, currentPage, pageSize]);

  const loadSellerRentals = useCallback(async (sellerId: number) => {
    setRentalsLoading(true);
    setRentalsError(false);
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

      const result = await rentalService.adminSearch({
        sellerIds: [sellerId],
        startDate,
        endDate,
        page: 0,
        size: 100,
      });
      setSellerRentals(result.content);
      setRentalsTotal(result.totalElements);
    } catch {
      setRentalsError(true);
    } finally {
      setRentalsLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadSellers();
  }, [loadSellers]);

  const handleSearch = () => {
    setCurrentPage(0);
    setAppliedName(filterName);
    setAppliedDocument(filterDocument);
  };

  const handleClearFilters = () => {
    setFilterName("");
    setFilterDocument("");
    setQuickSearch("");
    setCurrentPage(0);
    setAppliedName("");
    setAppliedDocument("");
  };

  const handlePageSizeChange = (value: string) => {
    setCurrentPage(0);
    setPageSize(Number(value));
  };

  const openCreate = () => {
    form.reset({ name: "", document: "" });
    setSelectedSeller(null);
    setDialogMode("create");
  };

  const openEdit = (seller: SellerResponseDTO) => {
    const rawDoc = seller.document.replace(/\D/g, "");
    const formattedDoc =
      rawDoc.length === 11 ? formatCPF(rawDoc) : seller.document;
    form.reset({ name: seller.name, document: formattedDoc });
    setSelectedSeller(seller);
    setDialogMode("edit");
  };

  const openDelete = (seller: SellerResponseDTO) => {
    setSelectedSeller(seller);
    setDialogMode("delete");
  };

  const openRentals = (seller: SellerResponseDTO) => {
    setSellerRentals([]);
    setRentalsTotal(0);
    setRentalsError(false);
    setSelectedSeller(seller);
    setIsRentalsModalOpen(true);
    loadSellerRentals(seller.id);
  };

  const closeDialog = () => setDialogMode(null);

  const closeRentalsModal = () => {
    setIsRentalsModalOpen(false);
    setSelectedSeller(null);
    setSellerRentals([]);
    setRentalsError(false);
  };

  const handleSubmit = async (data: SellerFormData) => {
    setSubmitLoading(true);
    try {
      if (dialogMode === "create") {
        const created = await sellerService.create(data);
        setSellers((prev) => [created, ...prev]);
        setTotalElements((prev) => prev + 1);
        toast.success("Vendedor criado com sucesso");
      } else if (selectedSeller) {
        const updated = await sellerService.update(selectedSeller.id, data);
        setSellers((prev) =>
          prev.map((s) =>
            s.id === updated.id
              ? {
                  ...s,
                  name: updated.name,
                  document: updated.document,
                  updatedAt: updated.updatedAt,
                }
              : s,
          ),
        );
        toast.success("Vendedor atualizado com sucesso");
      }
      closeDialog();
    } catch (error: unknown) {
      if (!applyFieldErrorsToForm(error, form)) {
        toast.error(
          getApiErrorMessage(
            error,
            dialogMode === "create"
              ? "Erro ao criar vendedor"
              : "Erro ao atualizar vendedor",
          ),
        );
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSeller) return;
    setSubmitLoading(true);
    try {
      await sellerService.remove(selectedSeller.id);
      setSellers((prev) => prev.filter((s) => s.id !== selectedSeller.id));
      setTotalElements((prev) => prev - 1);
      toast.success("Vendedor excluído com sucesso");
      closeDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir vendedor"));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Vendedores</h1>
          <p className="text-muted-foreground">Gerencie sua equipe de vendas</p>
        </div>
        <Can permission={ACTIONS.sellers.create}>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Vendedor
          </Button>
        </Can>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
          <CardDescription>Busque e filtre vendedores</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-medium">Nome</label>
              <Input
                placeholder="Ex: Carlos Mendes"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-medium">Documento</label>
              <Input
                placeholder="Ex: 111.222.333-44"
                value={filterDocument}
                onChange={(e) => setFilterDocument(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSearch} className="shrink-0">
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
            <Button onClick={handleClearFilters} variant="outline">
              Limpar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-base">Lista de Vendedores</CardTitle>
            <CardDescription className="mt-1">
              {quickSearch
                ? `${filteredSellers.length} de ${totalElements} ${totalElements === 1 ? "vendedor" : "vendedores"}`
                : `${totalElements} ${totalElements === 1 ? "vendedor" : "vendedores"}`}
              {appliedName && " (filtrado por nome)"}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Filtrar por nome ou CPF..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              className="w-52"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={loadSellers}
              title="Atualizar"
              disabled={loading}
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value={10}>10 / página</option>
              <option value={25}>25 / página</option>
              <option value={50}>50 / página</option>
              <option value={100}>100 / página</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          <SellersTable
            sellers={filteredSellers}
            loading={loading}
            currentPage={currentPage}
            totalPages={totalPages}
            onEdit={openEdit}
            onDelete={openDelete}
            onView={openRentals}
            onPageChange={setCurrentPage}
          />
        </CardContent>
      </Card>

      <SellerFormDialog
        open={dialogMode === "create" || dialogMode === "edit"}
        mode={dialogMode === "edit" ? "edit" : "create"}
        form={form}
        loading={submitLoading}
        onClose={closeDialog}
        onSubmit={handleSubmit}
      />

      <SellerDeleteDialog
        open={dialogMode === "delete"}
        seller={selectedSeller}
        loading={submitLoading}
        onClose={closeDialog}
        onConfirm={handleDelete}
      />

      {selectedSeller && (
        <SellerRentalsModal
          open={isRentalsModalOpen}
          seller={selectedSeller}
          rentals={sellerRentals}
          rentalsLoading={rentalsLoading}
          rentalsError={rentalsError}
          rentalsTotal={rentalsTotal}
          onClose={closeRentalsModal}
        />
      )}
    </div>
  );
}
